import React from 'react';
import {
    Alert,
    Box,
    Pagination,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import type {Contract} from '../../types/contract';
import ContractTableComponentMobile from './mobile/ContractTableComponentMobile';

interface ContractTableComponentProps {
    selectedEmployeeContract: Contract | null;
    contractsPage: number;
    contractsTotalRecords: number;
    onContractsPageChange: (page: number) => Promise<void>;
    sx?: any;
}

const ContractTableComponent: React.FC<ContractTableComponentProps> = ({
                                                                           selectedEmployeeContract,
                                                                           contractsPage,
                                                                           contractsTotalRecords,
                                                                           onContractsPageChange,
                                                                           sx
                                                                       }) => {
    const {t} = useTranslation();
    const formatEUR = (value: string | undefined): string => {
        if (value === undefined || value === null || value === '') return '-';
        const num = Number(String(value).replace(/[^0-9.-]+/g, ''));
        if (!isFinite(num)) return String(value);
        try {
            return new Intl.NumberFormat(undefined, {style: 'currency', currency: 'EUR'}).format(num);
        } catch (e) {
            return `€${num.toFixed(2)}`;
        }
    };
    // Since we always show 1 contract per page, totalPages equals contractsTotalRecords
    const totalPages = contractsTotalRecords;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        // Convert from 1-based UI to 0-based internal state
        onContractsPageChange(page - 1);
    };


    return (
        <Box sx={sx}>
            <Typography variant="h6" sx={{mb: 2}}>
                {t('employee.contract.detailsTitle', {count: contractsTotalRecords})}
            </Typography>

            {isMobile ? (
                <ContractTableComponentMobile
                    selectedEmployeeContract={selectedEmployeeContract}
                    contractsPage={contractsPage}
                    contractsTotalRecords={contractsTotalRecords}
                    onContractsPageChange={onContractsPageChange}
                    sx={{}}
                />
            ) : selectedEmployeeContract ? (
                <>
                    <TableContainer component={Paper}>
                        <Table>
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold', width: '50%'}}>{t('employee.contract.field.contractNumber')}</TableCell>
                                    <TableCell>{selectedEmployeeContract.contractNumber}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('employee.contract.field.startDate')}</TableCell>
                                    <TableCell>{selectedEmployeeContract.startDate}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('employee.contract.field.endDate')}</TableCell>
                                    <TableCell>{selectedEmployeeContract.endDate}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('employee.contract.field.jobPosition')}</TableCell>
                                    <TableCell>{selectedEmployeeContract.jobPosition}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('employee.contract.field.payrollCalcType')}</TableCell>
                                    <TableCell>{selectedEmployeeContract.payrollCalcType}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('employee.contract.field.salaryBasis')}</TableCell>
                                    <TableCell>{selectedEmployeeContract.salaryBasis}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('employee.contract.field.baseAmount')}</TableCell>
                                    <TableCell>{formatEUR(selectedEmployeeContract.baseAmount)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('employee.contract.field.employerInsuranceBenefit')}</TableCell>
                                    <TableCell>{formatEUR(selectedEmployeeContract.employerInsuranceBenefit)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('employee.contract.field.familyInsuranceDeduction')}</TableCell>
                                    <TableCell>{formatEUR(selectedEmployeeContract.familyInsuranceDeduction)}</TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {/* Pagination Controls */}
                    {totalPages > 1 && (
                        <Box sx={{mt: 3, display: 'flex', justifyContent: 'center', width: '100%'}}>
                            <Pagination
                                count={totalPages}
                                page={contractsPage + 1}
                                onChange={handlePageChange}
                                color="primary"
                                showFirstButton={totalPages > 10}
                                showLastButton={totalPages > 10}
                                siblingCount={totalPages <= 10 ? totalPages : 1}
                                boundaryCount={totalPages <= 10 ? 0 : 1}
                            />
                        </Box>
                    )}
                </>
            ) : (
                <Alert severity="info">{t('employee.contract.noneYet')}</Alert>
            )}
        </Box>
    );
};

export default ContractTableComponent;